import React, { useEffect, useState } from "react";
import { Form, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useSetRecoilState } from "recoil";
import { alertState } from "src/recoil/atoms/global";
import { patchSimulator } from "src/services/simulator-flight";
import ProtectedComponent from "src/components/Permission";
import TooltipWrapper from "src/components/Common/TooltipWrapper";
import { useMutation } from "src/Hooks/useReactQuery";
import FormTextarea from "src/components/Common/Form/FormTextarea";

const FORM_NAME = {
  NOTE: "note",
};

const ModalEditSimulation = ({ simData, onSuccess }) => {
  const { t } = useTranslation("flight-simulator", "common");
  const [toggle, setToggle] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const showAlert = useSetRecoilState(alertState);

  const editSimulationMutate = useMutation(
    {
      mutationFn: ({ data }) => patchSimulator({ id: data.id, data }),
    },
    true
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({});

  useEffect(() => {
    if (!simData || !toggle) return;

    // Only reset if this is a new modal opening or if the simulation ID changed
    if (!initialData || initialData.id !== simData.id) {
      setInitialData(simData);
      reset({
        [FORM_NAME.NOTE]: `${simData.note ?? ""}`,
      });
    }
  }, [toggle, simData, reset, initialData]);

  const handleSubmitForm = async (data) => {
    const inputData = {
      id: simData.id,
      note: data[FORM_NAME.NOTE],
    };
    try {
      await editSimulationMutate.mutateAsync({ data: inputData });
      setToggle(false);
      setInitialData(null);
      onSuccess();
      showAlert({
        status: true,
        message: t("edit.success"),
        type: "success",
      });
    } catch (err) {
      setToggle(false);
      setInitialData(null);
      showAlert({
        status: true,
        message: err?.response?.data?.error?.message || t("over_time"),
        type: "danger",
      });
    }
  };

  return (
    <div>
      <TooltipWrapper tooltipText={t("edit.button")} id={`edit-sim-${simData?.id}`}>
        <button
          onClick={() => {
            // reset();
            setInitialData(null);
            setToggle(true);
          }}
          className="btn btn-blank border-0 px-0"
          onFocus={(e) => {
            e.target.blur();
          }}
        >
          <i className="bi bi-pencil f-s-20 text-dark" />
        </button>
      </TooltipWrapper>

      <Modal
        centered
        isOpen={toggle}
        toggle={() => {
          setToggle(false);
          setInitialData(null);
        }}
        size="md"
        backdrop="static"
        style={{ maxWidth: "880px", width: "85%" }}
      >
        <ModalHeader
          toggle={() => {
            setToggle(false);
            setInitialData(null);
          }}
        >
          {t("edit.title")}
        </ModalHeader>
        <ModalBody className="p-b-0 overflow-auto">
          <Form className="p-x-15">
            <div className="row">
              <div className="col-md-12">
                <FormTextarea
                  register={register}
                  label={t(`new.${FORM_NAME.NOTE}`)}
                  name={FORM_NAME.NOTE}
                  placeholder={t("new.note_placeholder")}
                  classInput="width-full"
                  errors={errors?.[FORM_NAME.NOTE]?.message}
                  maxLength={2000}
                  maxLengthMessage={t("new.note_maxlength")}
                  rows={4}
                />
              </div>
            </div>
          </Form>
        </ModalBody>
        <ModalFooter className="justify-content-center pt-0 p-b-20">
          <button
            className="btn btn-sm btn-secondary m-x-10 width-by-px-120"
            onClick={() => {
              setToggle(false);
              setInitialData(null);
              document.body.click();
            }}
          >
            {t("new.cancel")}
          </button>
          <ProtectedComponent id="edit-simmulation" ownerId={simData.created_by}>
            <button
              className="btn btn-sm btn-primary m-x-10 width-by-px-120"
              disabled={editSimulationMutate.isLoading || !isDirty}
              onClick={handleSubmit((data) => handleSubmitForm(data))}
            >
              {t("new.submit")}
            </button>
          </ProtectedComponent>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ModalEditSimulation;
